import {
  Address, BN, bnToUnpaddedBuffer, rlp, toBuffer,
} from 'ethereumjs-util';
import { ITxData } from 'web3-provider-engine/subproviders/hooked-wallet';
import { AccessLists } from './eip1559AccessList';

export interface ItxData extends ITxData {
  chainId?: string | number,
  v?: string,
  r?: string,
  s?: string,
  maxFeePerGas?: string,
  maxPriorityFeePerGas?: string,
  type?: string,
  accessList: [],
}

const TRANSACTION_TYPE = 2;
const TRANSACTION_TYPE_BUFFER = Buffer.from(TRANSACTION_TYPE.toString(16).padStart(2, '0'), 'hex');
const eip1559SupportedNetworks = [1, 3, 4, 5, 42];

export class Transaction {
  rawTx: any[];

  public readonly isEIP1559: boolean;

  private signed: boolean;

  private readonly networkId: number;

  constructor(txData: ItxData, networkId: number) {
    const {
      nonce, gasPrice, value, data,
    } = txData;

    this.isEIP1559 = eip1559SupportedNetworks.includes(networkId) && parseInt(txData.type, 16) === 2;
    this.networkId = networkId;
    /*
          Implementation of @ethereumjs/tx with only required functionality
         */
    const gasLimit = txData.gas || txData.gasLimit;
    const toBuf = toBuffer(txData.to === '' ? '0x' : txData.to);
    const to = toBuf.length > 0 ? new Address(toBuf) : undefined;

    // from @ethereumjs/tx instance we need only hex-rlp
    this.rawTx = this.isEIP1559 ? [
      bnToUnpaddedBuffer(new BN(toBuffer(txData.chainId))),
      bnToUnpaddedBuffer(new BN(toBuffer(nonce === '' ? '0x' : nonce))),
      bnToUnpaddedBuffer(new BN(toBuffer(txData.maxPriorityFeePerGas === '' ? '0x' : txData.maxPriorityFeePerGas))),
      bnToUnpaddedBuffer(new BN(toBuffer(txData.maxFeePerGas === '' ? '0x' : txData.maxFeePerGas))),
      bnToUnpaddedBuffer(new BN(toBuffer(gasLimit === '' ? '0x' : gasLimit))),
      to !== undefined ? to.buf : Buffer.from([]),
      bnToUnpaddedBuffer(new BN(toBuffer(value === '' ? '0x' : value))),
      toBuffer(data === '' ? '0x' : data),
      AccessLists.getAccessListData(txData.accessList ?? []).accessList,
      Buffer.from([]),
      Buffer.from([]),
      Buffer.from([]),
    ] : [
      bnToUnpaddedBuffer(new BN(toBuffer(nonce === '' ? '0x' : nonce))),
      bnToUnpaddedBuffer(new BN(toBuffer(gasPrice === '' ? '0x' : gasPrice))),
      bnToUnpaddedBuffer(new BN(toBuffer(gasLimit === '' ? '0x' : gasLimit))),
      to !== undefined ? to.buf : Buffer.from([]),
      bnToUnpaddedBuffer(new BN(toBuffer(value === '' ? '0x' : value))),
      toBuffer(data === '' ? '0x' : data),
      networkId ? new BN(networkId) : Buffer.from([]),
      Buffer.from([]),
      Buffer.from([]),
    ];
    const { r, s, v } = txData;
    if (v && r && s) {
      this.sign({ r, s, v });
    }
  }

  sign({ r, s, v }: { r: string, s: string, v: string }) {
    // Adding { r, s, v } to our raw transaction to make it signed
    const vBN = new BN(toBuffer(v?.startsWith('0x') ? v : `0x${v}`));
    const rBN = new BN(toBuffer(r?.startsWith('0x') ? r : `0x${r}`));
    const sBN = new BN(toBuffer(s?.startsWith('0x') ? s : `0x${s}`));
    if (this.isEIP1559) {
      this.rawTx[9] = bnToUnpaddedBuffer(vBN);
      this.rawTx[10] = bnToUnpaddedBuffer(rBN);
      this.rawTx[11] = bnToUnpaddedBuffer(sBN);
    } else {
      this.rawTx[6] = vBN;
      this.rawTx[7] = rBN;
      this.rawTx[8] = sBN;
    }
    this.signed = true;
  }

  serialize() {
    return this.isEIP1559 ? Buffer.concat([TRANSACTION_TYPE_BUFFER, rlp.encode(this.rawTx as any)]) : rlp.encode(this.rawTx);
  }

  getMessageToSign() {
    if (this.isEIP1559) {
      const base = this.rawTx.slice(0, 9);
      return Buffer.concat([TRANSACTION_TYPE_BUFFER, rlp.encode(base as any)]);
    }
    return this.serialize();
  }

  validateSignature() {
    // Apply v check for Legacy transactions
    if (!this.signed) {
      throw new Error('Transaction is not signed!');
    }
    if (this.isEIP1559) {
      return true;
    }
    const v = this.rawTx[6].toNumber();
    // EIP155: v should be chain_id * 2 + {35, 36}
    const signedChainId = Math.floor((v - 35) / 2);
    if (signedChainId !== this.networkId) {
      throw new Error(`Invalid networkId signature returned. Expected: ${this.networkId}, Got: ${signedChainId}, v: ${v}`);
    }
    return true;
  }
}